import { notification } from "antd"
import { User2Icon } from "lucide-react"
import { lazy, Suspense, useState } from "react"
import { useTranslation } from "react-i18next"
import { useParams, useSearchParams } from "react-router"

import CustomTooltip from "~/components/custom-tooltip"
import {
  isPmbV1,
  openPmbV2Workbench,
  PMB_V2_MISSING_LINKEDIN_MESSAGE
} from "~/containers/pre-meeting-brief-v2"
import { toPlainStringForApi } from "~/utils/location-helpers"

// Legacy v1 drawer — only mounted when enable_agent_v2 is OFF. Lazy so
// the v1 bundle doesn't ship to v2-flag-on tenants.
const PersonBriefDrawer = lazy(() => import("./person-brief-drawer"))

/**
 * Pre-meeting Brief CTA used on every person card (recommended-person,
 * person-card, etc.). PMB v2 routes through ASKOS — clicking opens the
 * PreMeetingBriefV2Workbench overlay on the current page (no navigation).
 * The workbench creates the ASKOS session, pre-creates the pm_brief row,
 * and sends the seeded message that the planner picks up to ask Q1/Q3.
 *
 * Two bits of defensive normalization are essential here, because callers
 * pass props from a variety of upstream data shapes:
 *
 *   1. `linkedin_url` may arrive as a `LocalizedStringField` object
 *      (`{en: "..."}`) instead of a raw string when the parent forwards
 *      a backend record verbatim. encodeURIComponent on an object yields
 *      the literal "[object Object]". toPlainStringForApi unwraps it.
 *
 *   2. `accountId` callers sometimes pass an Apollo ObjectId (24-char
 *      hex) where we'd expect the internal integer account id. Both
 *      kinds need to reach the agent so it can do the right lookups,
 *      so we send them on different query params (?account vs ?apollo).
 *      The button auto-detects which is which when only one prop is
 *      passed.
 */

const APOLLO_ID_RE = /^[0-9a-f]{24}$/i

function classifyId(value: number | string | null | undefined) {
  if (value == null || value === "") {
    return {
      account: undefined as number | undefined,
      apollo: undefined as string | undefined
    }
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return { account: value, apollo: undefined }
  }
  const str = String(value).trim()
  if (!str) return { account: undefined, apollo: undefined }
  if (APOLLO_ID_RE.test(str)) return { account: undefined, apollo: str }
  // Looks like a stringified internal id ("7991").
  const asInt = Number(str)
  if (Number.isFinite(asInt) && /^\d+$/.test(str)) {
    return { account: asInt, apollo: undefined }
  }
  // Unknown shape — treat as apollo to be safe; Node controller stores it
  // in pm_brief.apollo_id (text NOT NULL) and the agent inspects both.
  return { account: undefined, apollo: str }
}

export default function GeneratePersonBriefButton({
  account_name,
  linkedin_url,
  personName,
  accountId,
  apolloId,
  opportunityId,
  showAsText,
  renderMessageTemplate
}: {
  account_name: string
  linkedin_url: string
  personName: string
  /** Internal account id (integer) — preferred when known. */
  accountId?: number | string
  /** Apollo ObjectId (24-char hex) — pass when an internal id isn't on hand. */
  apolloId?: string
  /** Opportunity id — override; resolved from route/query when not passed. */
  opportunityId?: number | string | null
  showAsText?: boolean
  /** Forwarded to the legacy v1 drawer when enable_agent_v2 is off. */
  renderMessageTemplate?: (props: {
    close: () => void
  }) => JSX.Element | null | any
}) {
  const { t } = useTranslation()
  // v1 flow: local state controls the legacy PersonBriefDrawer.
  const [isV1DrawerOpen, setIsV1DrawerOpen] = useState(false)
  // Opp id sources, in priority order:
  //   1. explicit `opportunityId` prop (caller knows best)
  //   2. `:priority_id` route param — convention on /accounts/:id/priority/:priority_id
  //   3. `?opp=` query param — convention on saas-overview + similar routes
  // (see app/hooks/priorities.ts comment for the ?opp= source of truth)
  const { priority_id } = useParams()
  const [searchParams] = useSearchParams()

  const handleClick = () => {
    // 1. Normalize linkedin_url — defensively unwrap LocalizedField objects.
    const normalizedLinkedin = (
      typeof linkedin_url === "string"
        ? linkedin_url
        : toPlainStringForApi(linkedin_url)
    ).trim()

    if (isPmbV1) {
      // v1 — open the legacy PersonBriefDrawer mounted below. It manages
      // its own data fetch from getSinglePersonBrief.
      if (!normalizedLinkedin) {
        notification.error({
          message: t("label_error"),
          description: PMB_V2_MISSING_LINKEDIN_MESSAGE
        })
        return
      }
      setIsV1DrawerOpen(true)
      return
    }

    // 2. Sort out which kind of account id we got (both optional — the
    //    v2 agent resolves the company from the LinkedIn URL when absent).
    const fromAccountProp = classifyId(accountId)
    const finalAccount = fromAccountProp.account
    const finalApollo = apolloId?.trim() || fromAccountProp.apollo

    if (!normalizedLinkedin) {
      notification.error({
        message: t("label_error"),
        description: PMB_V2_MISSING_LINKEDIN_MESSAGE
      })
      return
    }

    const resolvedOpportunity =
      opportunityId != null && String(opportunityId).trim() !== ""
        ? String(opportunityId)
        : (priority_id ?? searchParams.get("opp") ?? "")

    openPmbV2Workbench({
      linkedinUrl: normalizedLinkedin,
      accountId: finalAccount ?? null,
      apolloId: finalApollo ?? null,
      opportunityId: resolvedOpportunity || null,
      personName,
      accountName: account_name,
      source: "person_card"
    })
  }

  // accountId may arrive as Apollo ObjectId — the v1 drawer's prop is
  // typed as number, so only forward when we have a numeric.
  const numericAccountId =
    typeof accountId === "number"
      ? accountId
      : typeof accountId === "string" && /^\d+$/.test(accountId)
        ? Number(accountId)
        : undefined

  return (
    <>
      <CustomTooltip
        title={
          <p className="text-xs text-center">
            {t("person_brief.label_tooltip_content")}
          </p>
        }
      >
        {showAsText ? (
          <button
            className="flex gap-1 items-center w-fit justify-end"
            onClick={handleClick}
            data-testid="generate-person-brief-text"
          >
            <User2Icon
              size={14}
              className="dark:!text-gray-300 text-dark w-4 h-4 min-w-4 min-h-4"
            />
            <p
              className={`text-primary text-left text-xs dark:text-primary-light`}
            >
              {t("label_pre_meeting_brief")}
            </p>
          </button>
        ) : (
          <button
            className="dark:text-dark-6 text-dark-2 p-2 border-[0.5px] border-light-border rounded-lg text-xs dark:bg-dark-3 bg-white"
            onClick={handleClick}
            data-testid="generate-person-brief-button"
          >
            {t("label_pre_meeting_brief")}
          </button>
        )}
      </CustomTooltip>
      {isPmbV1 && isV1DrawerOpen && (
        <Suspense fallback={null}>
          <PersonBriefDrawer
            account_name={account_name}
            linkedin_url={linkedin_url}
            personName={personName}
            accountId={numericAccountId}
            renderMessageTemplate={renderMessageTemplate}
            isOpen={isV1DrawerOpen}
            setIsOpen={setIsV1DrawerOpen}
          />
        </Suspense>
      )}
    </>
  )
}
